import { Component, OnInit, Output, EventEmitter, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from "@angular/common";
import { UserRolesService } from '../../services/user-roles.service';
import { AddSubscription } from '../../services/add-subscription.service';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError } from 'rxjs/operators';
import { MasterDataService } from '../../services/masterdata.service';

@Component({
    selector: 'app-add-edit-pricing-modal',
    templateUrl: './add-edit-pricing-modal.component.html',
    styleUrls: ['./add-edit-pricing-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddEditPricingModalComponent implements OnInit {
  @Input() type:string;
  @Input() pricingOption:string;
  @Input() startDate:string;
  @Input() currencySign:string;
  @Input() editData: any;
  @Input() endDate: string;
  @Input() tableLength:number;
  @Input() isEditMode: boolean;
  @Input() sponsoringCountry: number;
  @Input() userRoleID: number;
  @Input() isWBSMandatory: boolean = false;
  @Input() isStatusActive: boolean = false;
  @Input() isSubscriptionCreatedBeforeMdmWbsDeploymentDate: boolean = true;
  @Input() isSubscriptionStatusNew: boolean = true;
  @Input() eventLink: string = '';
  @Output() pricingDetails = new EventEmitter();
  pricingForm: FormGroup;
  isSubmitted:boolean = false;
  formatErrorMessage:string = '';
  splitErrorMessage:string = "";
  isPercentageSplit100:boolean = true;
  isSurChargePercentageSplit100: boolean = true;
  isOpenPricing: boolean = false;
  isAddWbsCodes: boolean = false;
  isAddSurChargeWbsCodes: boolean = false;
  isDuplicateWbsCodes: boolean = false;
  isDuplicateSurchargeWbsCodes : boolean  = false;
  isSurchargeFormatError: boolean = false;
  isWbsFormatError: boolean = false;
  disableNoOfUsers: boolean = false;
  invalidNumberOfUsers: boolean = false;
  minNumberOfUsers: number = 0;
  // WBS typeahead properties
  wbsCodeInput$ = new Subject<string>();
  searchingWBS: boolean = false;
  wbsRowSearchState: { [key: string]: boolean } = {};
  private wbsTypeaheadHandlers: { [key: string]: (text$: Observable<string>) => Observable<any[]> } = {};
  searchFailedWBS: boolean = false;
  invalidWBSDunsID: boolean = false;
  isWbsStatusChecking: boolean = false;
  isWbsInactive: boolean = false;
  inactiveWbsCode: string = '';
  wbsNotPickedFromList: boolean = false;
  IsWBSCodeConfirmed: boolean = false;
  sfdcDisableReasons: any[] = [];
  isSFDCDisableOtherReasonInvalid: boolean = false;
  sfdcDisableOtherReasonInvalidMessage: string = 'Please enter a reason';
  //userRoleID:any = "";
  constructor(public activeModal: NgbActiveModal,private _formBuilder: FormBuilder, private datepipe: DatePipe,
    private userRoleService: UserRolesService, private addSubscriptionService: AddSubscription,
    private masterDataService: MasterDataService) { }

  get showPrepaidSfdcControls(): boolean {
    return !this.isOpenPricing && !this.isSubscriptionStatusNew && +this.userRoleID === 1;
  }

  get eventIdValue(): string {
    return `${this.pricingForm?.get('EventID')?.value || ''}`.trim();
  }

  private shouldLockInactiveExistingWbs(): boolean {
    return this.isEditMode && this.isSubscriptionCreatedBeforeMdmWbsDeploymentDate;
  }

  private shouldValidateExistingWbsOnLoad(): boolean {
    return this.type === 'edit';
  }

  ngOnInit() {
    this.isOpenPricing = this.pricingOption === 'Open' ? true : false;
    this.loadSFDCDisableReasons();
    this.initializeForm();
    this.userRoleService.getNavigationProperty().subscribe((res) => {
      if(!res){
        this.activeModal.close();
      }
    });
    this.setPricePerPerson();
    if (this.userRoleID != 1 && this.userRoleID != 3) {
      this.minNumberOfUsers = 100;
    } else {
      this.minNumberOfUsers = 0;
    }
    this.initializeWBSTypeahead();
  }

  loadSFDCDisableReasons() {
    this.masterDataService.getMasterDataDetails().subscribe((masterData) => {
      this.sfdcDisableReasons = masterData?.SFDCDisableReasons
        ? JSON.parse(JSON.stringify(masterData.SFDCDisableReasons))
        : [];
    });
  }

  initializeWBSTypeahead() {
    // Initialize WBS typeahead stream for pricing modal
    // This provides real-time WBS code suggestions as user types
    // Also trigger validation for existing WBS codes whenever an existing pricing row is opened.
    // Open pricing rows can also be edited while creating a new subscription, where `isEditMode`
    // is false even though the modal itself is opened in edit mode.
    if (this.shouldValidateExistingWbsOnLoad()) {
      this.validateExistingWBSCodes();
    }
  }

  validateExistingWBSCodes() {
    // In edit mode, validate existing WBS codes against API
    // Check if validation status is already set by parent component
    if (this.editData && this.editData.WBSCodesList && this.editData.WBSCodesList.length > 0) {
      this.editData.WBSCodesList.forEach((wbsItem: any, index: number) => {
        if (wbsItem.wbscode && wbsItem.wbscode.trim()) {
          // Check if validation was already done in parent component
          if (wbsItem['isWbsStatusChecked']) {
            // Use pre-validated status from parent component
            const formArray = this.pricingForm.get('WBSCodesList');
            if (formArray && formArray['controls'] && formArray['controls'][index]) {
              formArray['controls'][index]['isExistingWbs'] = true;
              formArray['controls'][index]['isWbsActive'] = wbsItem['isWbsActive'];
              formArray['controls'][index]['isWbsStatusChecked'] = true;
              // Disable the field for inactive existing WBS so it is greyed out
              if (!wbsItem['isWbsActive'] && this.shouldLockInactiveExistingWbs()) {
                formArray['controls'][index].get('wbscode').disable();
              }
            }
          } else {
            // Validation not done yet, call API
            this.validateWBSCodeStatus(wbsItem.wbscode, index, false);
          }
        }
      });
    }
    
    // Also validate surcharge WBS codes if present
    if (this.editData && this.editData.SurchargeWBSCodesList && this.editData.SurchargeWBSCodesList.length > 0) {
      this.editData.SurchargeWBSCodesList.forEach((wbsItem: any, index: number) => {
        if (wbsItem.wbscode && wbsItem.wbscode.trim()) {
          // Check if validation was already done in parent component
          if (wbsItem['isWbsStatusChecked']) {
            // Use pre-validated status from parent component
            const formArray = this.pricingForm.get('SurchargeWBSCodesList');
            if (formArray && formArray['controls'] && formArray['controls'][index]) {
              formArray['controls'][index]['isExistingWbs'] = true;
              formArray['controls'][index]['isWbsActive'] = wbsItem['isWbsActive'];
              formArray['controls'][index]['isWbsStatusChecked'] = true;
              if (!wbsItem['isWbsActive'] && this.shouldLockInactiveExistingWbs()) {
                formArray['controls'][index].get('wbscode').disable();
              }
            }
          } else {
            // Validation not done yet, call API
            this.validateWBSCodeStatus(wbsItem.wbscode, index, true);
          }
        }
      });
    }
  }

  validateWBSCodeStatus(wbsCode: string, itemIndex: number, isSurcharge: boolean) {
    if (!wbsCode || wbsCode.length < 22) {
      return;
    }

    this.isWbsStatusChecking = true;
    this.addSubscriptionService.getWBSCodes(wbsCode).subscribe((results) => {
      const normalizedRequest = this.normalizeWbsCode(wbsCode);
      const matches = (results || []).some((entry: any) => {
        const entryCode = this.normalizeWbsCode(
          entry?.Element || entry?.WBSCode || entry?.wbsCode
        );
        return entryCode && entryCode === normalizedRequest;
      });

      // Update the form control with status information
      const controls = this.pricingForm.get(isSurcharge ? 'SurchargeWBSCodesList' : 'WBSCodesList')['controls'];
      
      if (controls && controls[itemIndex]) {
        // This method is only called for existing WBS codes (from editData)
        controls[itemIndex]['isExistingWbs'] = true;
        controls[itemIndex]['isWbsStatusChecked'] = true;
        if (matches) {
          // WBS is active - mark as valid (green tick)
          controls[itemIndex]['isWbsActive'] = true;
          controls[itemIndex]['isSelectedFromPicklist'] = true;
          controls[itemIndex]['selectedWbsCode'] = this.normalizeWbsCode(wbsCode);
        } else {
          // WBS is inactive – grey tick, disable the field so it is non-editable
          controls[itemIndex]['isWbsActive'] = false;
          if (this.shouldLockInactiveExistingWbs()) {
            controls[itemIndex].get('wbscode').disable();
          }
          // Do NOT set isWbsInactive here: inactive existing codes must not block saving
        }
      }
      this.isWbsStatusChecking = false;
    });
  }

  normalizeWbsCode(code: string): string {
    return (code || '').toUpperCase().trim();
  }

  private getWbsRowKey(itemId: number, isSurcharge: boolean): string {
    return `${isSurcharge ? 'surcharge' : 'chargeback'}-${itemId}`;
  }

  private setWbsRowSearching(itemId: number, isSurcharge: boolean, isSearching: boolean): void {
    const key = this.getWbsRowKey(itemId, isSurcharge);
    this.wbsRowSearchState[key] = isSearching;
  }

  isWbsRowSearching(itemId: number, isSurcharge: boolean): boolean {
    const key = this.getWbsRowKey(itemId, isSurcharge);
    return !!this.wbsRowSearchState[key];
  }
  
  setPricePerPerson() {
    if (this.userRoleID != 1 && this.userRoleID != 3 && this.sponsoringCountry == 23 ) {
      this.pricingForm.get('PricePerPerson').setValue(80);
    }
  }
  validateNumberOfUser() {
    var val = this.pricingForm.get('NoOfUsers').value;
    if (this.userRoleID != 1 && this.userRoleID != 3 && val < 100) {
      this.invalidNumberOfUsers = true;
    } else {
      this.invalidNumberOfUsers = false;
    }
  }
  //getUserRoleList() {
  //      this.userRoleService.getUserRoles().subscribe((res) => {
  //          this.userRoleID = res.HighestRoleId;           
  //      });
  //  }

  // Check if No of Users is to be disabled
  isDisableNoOfUsers(): boolean {

    if ((this.isEditMode && this.editData['EventID']) || (this.isEditMode && +this.editData['ActualUsage'] > 0 && this.editData.id < this.tableLength)) {
      return true;
    }

    return false;
  }

  initializeForm():void {
    if(this.isOpenPricing){
      this.pricingForm = this._formBuilder.group({
            StartDate: new FormControl({value:this.datepipe.transform(new Date(this.editData.StartDate), 'MM/dd/yyyy'), disabled: true}),
            EndDate: new FormControl({value:this.datepipe.transform(new Date(this.editData.EndDate), 'MM/dd/yyyy'), disabled: true}),
            PricePerPerson: new FormControl(this.editData.PricePerPerson),
            ChargeBack: new FormControl({value: this.editData.ChargeBack, disabled:true}),
            ActualUsage: new FormControl({ value: this.editData.ActualUsage, disabled: true }),
              IsWBSCodeConfirmed: new FormControl(!!this.editData.IsWBSCodeConfirmed),
            WBSCodesList: this._formBuilder.array([])
          });
          this.setWbsCodes();
    } else {
      const wbscodeValidators = this.sponsoringCountry != 23
        ? [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]
        : [Validators.required, Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)];
      if(this.type === 'edit'){
        this.pricingForm = this._formBuilder.group({
          DateOfPurchase: new FormControl({value:this.editData.DateOfPurchase, disabled: true}),
          EventID: new FormControl({ value: this.editData.EventID ? this.editData.EventID : '', disabled: true }),
          EventName: new FormControl({ value: this.editData.EventName ? this.editData.EventName : '', disabled: true }),
          PricePerPerson: new FormControl({
                     value: this.editData.PricePerPerson, 
                     disabled: (this.isEditMode ? this.editData['EventID'] : false) || (this.userRoleID != 1 && this.userRoleID != 3 && this.sponsoringCountry == 23)
                   }),
          NoOfUsers:  new FormControl({ value: this.editData.NoOfUsers, disabled: this.isDisableNoOfUsers()}, Validators.required) ,
          ChargeBack: new FormControl({value: this.editData.ChargeBack, disabled: true}),
          Surcharge: new FormControl((+this.editData['Surcharge'] > 0) ? (+this.editData['Surcharge']) : 0),
          DisableSFDCIntegration: new FormControl(!!this.editData.DisableSFDCIntegration),
          SFDCDisableReasonId: new FormControl(this.editData.SFDCDisableReasonId ? this.editData.SFDCDisableReasonId : null),
          SFDCDisableOtherReason: new FormControl(this.editData.SFDCDisableOtherReason ? this.editData.SFDCDisableOtherReason : ''),
          IsWBSCodeConfirmed: new FormControl(!!this.editData.IsWBSCodeConfirmed),
          WBSCodesList: this._formBuilder.array([])
        });
        if(this.isEditMode && +this.editData['Surcharge'] > 0){
          this.setSurchargeWbsCodes();
        }
        this.setWbsCodes();
        this.calculateChargeback();
      }else{
        this.pricingForm = this._formBuilder.group({
          DateOfPurchase: new FormControl({ value: this.datepipe.transform(new Date(), 'MM/dd/yyyy'), disabled: true }),
          EventID: new FormControl({ value: '', disabled: true }),
          EventName: new FormControl({ value: '', disabled: true }),
          PricePerPerson: new FormControl({ value: null, disabled: this.userRoleID != 1 && this.userRoleID != 3 && this.sponsoringCountry == 23 ? true : false }),
          NoOfUsers:  new FormControl(null, Validators.required),
          ChargeBack: new FormControl({value:'', disabled: true}),
          DisableSFDCIntegration: new FormControl(false),
          SFDCDisableReasonId: new FormControl(null),
          SFDCDisableOtherReason: new FormControl(''),
          WBSCodesList: this._formBuilder.array([this._formBuilder.group({
            wbscode: new FormControl('', wbscodeValidators),
            Description: new FormControl({value:'', disabled: true})
          })])
        });
      }
    }

    this.initializeSFDCIntegrationControls();

    if (this.isEditMode) {
      this.disableForm();
    }

    this.IsWBSCodeConfirmed = this.pricingForm?.get('IsWBSCodeConfirmed')?.value;
  
    
  }
  disableForm() {
    // Disable all fields if userRoleID is 6 (subs admin)
    if (this.userRoleID === 6 && this.pricingForm) {
      Object.keys(this.pricingForm.controls).forEach(controlName => {
        const control = this.pricingForm.controls[controlName];
        control.disable();
        // Perform any operations needed on each control
      });
    }
  }

  onWbsConfirmationChange(event: any): void {
    this.IsWBSCodeConfirmed = event.target.checked;
    const confirmationControl = this.pricingForm?.get('IsWBSCodeConfirmed');
    if (confirmationControl) {
      confirmationControl.setValue(this.IsWBSCodeConfirmed);
    }
  }

  initializeSFDCIntegrationControls(): void {
    if (this.isOpenPricing || !this.pricingForm) {
      return;
    }

    const disableCtrl = this.pricingForm.get('DisableSFDCIntegration');
    const reasonCtrl = this.pricingForm.get('SFDCDisableReasonId');
    const otherReasonCtrl = this.pricingForm.get('SFDCDisableOtherReason');

    if (!disableCtrl || !reasonCtrl || !otherReasonCtrl) {
      return;
    }

    if (!this.showPrepaidSfdcControls) {
      disableCtrl.setValue(false, { emitEvent: false });
      disableCtrl.disable({ emitEvent: false });
      reasonCtrl.disable({ emitEvent: false });
      otherReasonCtrl.disable({ emitEvent: false });
      this.pricingForm.get('EventID')?.disable({ emitEvent: false });
      this.pricingForm.get('EventName')?.disable({ emitEvent: false });
      return;
    }

    this.updateSfdcIntegrationFormState();
    disableCtrl.valueChanges.subscribe(() => {
      this.updateSfdcIntegrationFormState();
    });
    reasonCtrl.valueChanges.subscribe(() => {
      this.updateSfdcIntegrationFormState();
    });
  }

  private updateSfdcIntegrationFormState(): void {
    const disableCtrl = this.pricingForm.get('DisableSFDCIntegration');
    const reasonCtrl = this.pricingForm.get('SFDCDisableReasonId');
    const otherReasonCtrl = this.pricingForm.get('SFDCDisableOtherReason');
    const eventIdCtrl = this.pricingForm.get('EventID');
    const eventNameCtrl = this.pricingForm.get('EventName');

    if (!disableCtrl || !reasonCtrl || !otherReasonCtrl || !eventIdCtrl) {
      return;
    }

    const isDisabledByUser = !!disableCtrl.value;
    const hasExistingEventId = !!(this.editData && this.editData.EventID);
    const hasExistingEventName = !!(this.editData && this.editData.EventName);

    reasonCtrl.clearValidators();
    otherReasonCtrl.clearValidators();

    if (isDisabledByUser) {
      reasonCtrl.enable({ emitEvent: false });
      reasonCtrl.setValidators([Validators.required]);
      if (+reasonCtrl.value === 4) {
        otherReasonCtrl.enable({ emitEvent: false });
        otherReasonCtrl.setValidators([Validators.required]);
      } else {
        otherReasonCtrl.setValue('', { emitEvent: false });
        otherReasonCtrl.disable({ emitEvent: false });
      }
    } else {
      // Preserve reason and other-reason values so they are restored when user re-checks.
      reasonCtrl.clearValidators();
      reasonCtrl.disable({ emitEvent: false });
      otherReasonCtrl.clearValidators();
      otherReasonCtrl.disable({ emitEvent: false });
      this.isSFDCDisableOtherReasonInvalid = false;

      if (!hasExistingEventId) {
        eventIdCtrl.setValue('', { emitEvent: false });
      }
      if (!hasExistingEventName) {
        eventNameCtrl?.setValue('', { emitEvent: false });
      }
    }

    const shouldEnableEventId =
      this.showPrepaidSfdcControls &&
      this.isStatusActive &&
      isDisabledByUser &&
      !hasExistingEventId;
    if (shouldEnableEventId) {
      eventIdCtrl.enable({ emitEvent: false });
    } else {
      eventIdCtrl.disable({ emitEvent: false });
    }

    const shouldEnableEventName =
      this.showPrepaidSfdcControls &&
      this.isStatusActive &&
      isDisabledByUser &&
      !hasExistingEventName;
    if (shouldEnableEventName) {
      eventNameCtrl?.enable({ emitEvent: false });
    } else {
      eventNameCtrl?.disable({ emitEvent: false });
    }

    reasonCtrl.updateValueAndValidity({ emitEvent: false });
    otherReasonCtrl.updateValueAndValidity({ emitEvent: false });
  }

  onFocusOutOtherReason() {
    const otherReasonCtrl = this.pricingForm.get('SFDCDisableOtherReason');
    if (otherReasonCtrl && `${otherReasonCtrl.value || ''}`.trim().length === 0) {
      otherReasonCtrl.setValue('');
    }
  }

  validateSFDCDisableOtherReason() {
    const otherReasonCtrl = this.pricingForm.get('SFDCDisableOtherReason');
    const reason = `${otherReasonCtrl?.value || ''}`;
    if (!otherReasonCtrl || !otherReasonCtrl.enabled) {
      this.isSFDCDisableOtherReasonInvalid = false;
      this.sfdcDisableOtherReasonInvalidMessage = '';
      return;
    }

    if (reason) {
      const regex = /^[0-9!@#\$%\^\&*\)\(+=._-]+$/g;
      const regex1 = /^[!@#\$%\^\&*\)\(+=._-]+$/g;
      if (
        reason.trim().length < 5 ||
        reason.trim().length > 1000 ||
        reason.trim() === 'N/A' ||
        reason.trim() === 'NA' ||
        regex.test(reason) ||
        regex1.test(reason)
      ) {
        this.isSFDCDisableOtherReasonInvalid = true;
        this.sfdcDisableOtherReasonInvalidMessage = 'Please enter a valid reason.';
        otherReasonCtrl.setErrors({ invalidOtherReason: true });
      } else {
        this.isSFDCDisableOtherReasonInvalid = false;
        this.sfdcDisableOtherReasonInvalidMessage = '';
        const existingErrors = otherReasonCtrl.errors || {};
        delete existingErrors['invalidOtherReason'];
        otherReasonCtrl.setErrors(Object.keys(existingErrors).length ? existingErrors : null);
      }
    } else {
      this.isSFDCDisableOtherReasonInvalid = true;
      this.sfdcDisableOtherReasonInvalidMessage = 'Please enter a reason';
      otherReasonCtrl.setErrors({ required: true });
    }
  }

  hasValidWBSCodes(): boolean {
    const wbsArray = this.pricingForm.get('WBSCodesList');
    if (!wbsArray || !wbsArray['controls'] || wbsArray['controls'].length === 0) {
      return false;
    }

    // Check if there are any valid WBS codes (either selected from picklist or existing active codes)
    return wbsArray['controls'].some((control: any) => {
      const wbsCode = control.controls['wbscode'].value;
      if (!wbsCode || !wbsCode.trim()) {
        return false;
      }
      // Valid if selected from picklist or if it's an existing active WBS code
      return control['isSelectedFromPicklist'] === true || 
             (control['isExistingWbs'] === true && control['isWbsActive'] === true);
    });
  }
  EnableDisableNoOfUsers():boolean {
    if(this.editData.id < this.tableLength){
     return true;
    }else{
      return false;
    }
  }

  setSurchargeWbsCodes():void {
    if(this.editData.SurchargeWBSCodesList){
      this.pricingForm.addControl('SurchargeWBSCodesList', this._formBuilder.array([]));
      if(this.editData.SurchargeWBSCodesList.length > 1){
        this.editData.SurchargeWBSCodesList.forEach((code) => {
          this.addSurchargeWbsCodes(code);
          // Mark as existing WBS and restore validation status from parent if available
          const ctrl = this.SurchargeWBSCodesList.at(this.SurchargeWBSCodesList.length - 1);
          ctrl['isExistingWbs'] = true;
          ctrl['originalWbsCode'] = this.normalizeWbsCode(code?.wbscode || '');
          if (code['isWbsStatusChecked']) {
            ctrl['isWbsActive'] = code['isWbsActive'];
            ctrl['isWbsStatusChecked'] = true;
            if (code['isWbsActive']) {
              ctrl['isSelectedFromPicklist'] = true;
              ctrl['selectedWbsCode'] = this.normalizeWbsCode(code?.wbscode || '');
            }
            if (!code['isWbsActive'] && this.shouldLockInactiveExistingWbs()) {
              ctrl.get('wbscode').disable();
            }
          }
        });
      } else if(this.editData.SurchargeWBSCodesList.length === 1){
        this.SurchargeWBSCodesList.push(
          this._formBuilder.group({
            wbscode: new FormControl(this.editData.SurchargeWBSCodesList[0].wbscode , [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]),
            Description: new FormControl({value: '', disabled: true}   )
          })
        );
        // Mark as existing WBS and restore validation status from parent if available
        const ctrl0 = this.SurchargeWBSCodesList.at(0);
        ctrl0['isExistingWbs'] = true;
        ctrl0['originalWbsCode'] = this.normalizeWbsCode(this.editData.SurchargeWBSCodesList[0]?.wbscode || '');
        if (this.editData.SurchargeWBSCodesList[0]['isWbsStatusChecked']) {
          ctrl0['isWbsActive'] = this.editData.SurchargeWBSCodesList[0]['isWbsActive'];
          ctrl0['isWbsStatusChecked'] = true;
          if (this.editData.SurchargeWBSCodesList[0]['isWbsActive']) {
            ctrl0['isSelectedFromPicklist'] = true;
            ctrl0['selectedWbsCode'] = this.normalizeWbsCode(this.editData.SurchargeWBSCodesList[0]?.wbscode || '');
          }
          if (!this.editData.SurchargeWBSCodesList[0]['isWbsActive'] && this.shouldLockInactiveExistingWbs()) {
            ctrl0.get('wbscode').disable();
          }
        }
        this.enableDisableSurchargeWbsCodes();
      } else {
        this.SurchargeWBSCodesList.push(
          this._formBuilder.group({
            wbscode: new FormControl('' , [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]),
            Description: new FormControl({value: '', disabled: true}   )
          })
        );
      }
    }
    else{
      this.pricingForm.addControl('SurchargeWBSCodesList', this._formBuilder.array([this._formBuilder.group({
        wbscode: new FormControl('', [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]),
        Description: new FormControl({value:'', disabled: true})
      })]));
      this.enableDisableSurchargeWbsCodes();
    }
  }

  setWbsCodes(): void{
    const wbscodeValidators = this.sponsoringCountry != 23
      ? [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]
      : [Validators.required, Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)];
    if(this.editData.WBSCodesList && this.editData.WBSCodesList.length > 1){
      this.editData.WBSCodesList.forEach((code) => {
        this.addWbsCodes(code);
        // Mark as existing WBS and restore validation status from parent if available
        const ctrl = this.WBSCodesList.at(this.WBSCodesList.length - 1);
        ctrl['isExistingWbs'] = true;
        ctrl['originalWbsCode'] = this.normalizeWbsCode(code?.wbscode || '');
        if (code['isWbsStatusChecked']) {
          ctrl['isWbsActive'] = code['isWbsActive'];
          ctrl['isWbsStatusChecked'] = true;
          if (code['isWbsActive']) {
            ctrl['isSelectedFromPicklist'] = true;
            ctrl['selectedWbsCode'] = this.normalizeWbsCode(code?.wbscode || '');
          }
          // Disable the input for inactive existing codes so it is greyed out
          if (!code['isWbsActive'] && this.shouldLockInactiveExistingWbs()) {
            ctrl.get('wbscode').disable();
          }
        }
      });
    } else if(this.editData.WBSCodesList && this.editData.WBSCodesList.length === 1){
      this.WBSCodesList.push(
        this._formBuilder.group({
          wbscode: new FormControl(this.editData.WBSCodesList[0].wbscode, wbscodeValidators),
          Description: new FormControl({value: '', disabled: true}   )
        })
      );
      // Mark as existing WBS and restore validation status from parent if available
      const ctrl0 = this.WBSCodesList.at(0);
      ctrl0['isExistingWbs'] = true;
      ctrl0['originalWbsCode'] = this.normalizeWbsCode(this.editData.WBSCodesList[0]?.wbscode || '');
      if (this.editData.WBSCodesList[0]['isWbsStatusChecked']) {
        ctrl0['isWbsActive'] = this.editData.WBSCodesList[0]['isWbsActive'];
        ctrl0['isWbsStatusChecked'] = true;
        if (this.editData.WBSCodesList[0]['isWbsActive']) {
          ctrl0['isSelectedFromPicklist'] = true;
          ctrl0['selectedWbsCode'] = this.normalizeWbsCode(this.editData.WBSCodesList[0]?.wbscode || '');
        }
        if (!this.editData.WBSCodesList[0]['isWbsActive'] && this.shouldLockInactiveExistingWbs()) {
          ctrl0.get('wbscode').disable();
        }
      }
      this.enableDisableAddWbsCode();
    } else{
      this.WBSCodesList.push(
        this._formBuilder.group({
          wbscode: new FormControl('', wbscodeValidators),
          Description: new FormControl({value: '', disabled: true}   )
        })
      );
      this.enableDisableAddWbsCode();
    }
  }

   //Getting form array
   get WBSCodesList() {
    return this.pricingForm?.get('WBSCodesList') as FormArray;
  }

  get SurchargeWBSCodesList() {
    return this.pricingForm?.get('SurchargeWBSCodesList') as FormArray;
  }

  addSurchargeWbsCodes(value?) {
    this.isSubmitted = false;
    this.isSurChargePercentageSplit100 = true;
    this.SurchargeWBSCodesList.push(
      this._formBuilder.group({
        wbscode: new FormControl(value && value.wbscode ? value.wbscode : '', [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]),
        Description: new FormControl(value && value.Description ? value.Description : '', [Validators.required])
      })
    );
    
    // Initialize selection flags for new surcharge WBS code entry
    const newIndex = this.SurchargeWBSCodesList.length - 1;
    const control = this.pricingForm.get('SurchargeWBSCodesList')['controls'][newIndex];
    if (control) {
      control['isSelectedFromPicklist'] = false;
      control['isWbsActive'] = false;
      control['isWbsStatusChecked'] = false;
      control['selectedWbsCode'] = null;
    }
    
    if(this.SurchargeWBSCodesList.length === 2){
      this.pricingForm.get('SurchargeWBSCodesList')['controls']
      .forEach(control => {
        control.controls['Description'].enable();
      })
    }
    this.enableDisableSurchargeWbsCodes();
  }

  // Adding WBSCodes
  addWbsCodes(value?) {
    this.isSubmitted = false;
    this.isPercentageSplit100 = true;
    const wbscodeValidators = this.sponsoringCountry != 23
      ? [Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)]
      : [Validators.required, Validators.pattern(/^[a-zA-Z]{3}(.){5}(-){1}(.){2}(-){1}(.){2}(-){1}(.){2}(-){1}(.){4}$/)];
    this.WBSCodesList.push(
      this._formBuilder.group({
        wbscode: new FormControl(value && value.wbscode ? value.wbscode : '', wbscodeValidators),
        Description: new FormControl(value && value.Description ? value.Description : '', [Validators.required])
      })
    );
    
    // Initialize selection flags for new WBS code entry
    const newIndex = this.WBSCodesList.length - 1;
    const control = this.pricingForm.get('WBSCodesList')['controls'][newIndex];
    if (control) {
      control['isSelectedFromPicklist'] = false;
      control['isWbsActive'] = false;
      control['isWbsStatusChecked'] = false;
      control['selectedWbsCode'] = null;
    }
    
    if(this.WBSCodesList.length === 2){
      this.pricingForm.get('WBSCodesList')['controls']
      .forEach(control => {
        control.controls['Description'].enable();
      })
    }
    this.enableDisableAddWbsCode();
  }

  removeSurChargeWbsCode(index):void {
    this.isSubmitted = false;
    this.isSurChargePercentageSplit100 = true;
    this.SurchargeWBSCodesList.removeAt(index);
    if(this.SurchargeWBSCodesList.length === 1){
      this.isSurchargeFormatError = false;
      this.pricingForm.get('SurchargeWBSCodesList')['controls']
      .forEach(control => {
        control.controls['Description'].setValue("");
        control.controls['Description'].disable();
      })
    }
    this.enableDisableSurchargeWbsCodes();
  }
  removeWbsCode(index):void {
    this.isPercentageSplit100 = true;
    this.isSubmitted = false;
    this.WBSCodesList.removeAt(index);
    if(this.WBSCodesList.length === 1){
      this.isWbsFormatError = false
      this.pricingForm.get('WBSCodesList')['controls']
      .forEach(control => {
        control.controls['Description'].setValue("");
        control.controls['Description'].disable();
      })
    }
      this.enableDisableAddWbsCode();
      this.checkWbsCodesErrors(this.pricingForm.get('WBSCodesList')['controls'], 'chargeback');
  }
  
  acceptPositiveNumberInput(event): boolean {
    const isPaste = event.type === 'paste';

    if (isPaste) {
      const pasteData = event.clipboardData?.getData('text') || '';
      if (pasteData.includes('.')) {
        event.preventDefault();
        return false;
      }
    }
    
    if(event.charCode === 46) {
      event.preventDefault();
      return false;
    }
    return event.charCode == 8 || event.charCode == 0 || event.charCode == 13
      ? null
      : event.charCode >= 48 && event.charCode <= 57;
  }

  checkPercentage(values,type:string):void {
    let totalPercentage = 0;
   values.forEach(control =>{
      totalPercentage = +totalPercentage + (+control.controls['Description'].value);
    })
    if(totalPercentage !== 100){
      type === 'surcharge' ? (this.isSurChargePercentageSplit100 = false) : (this.isPercentageSplit100 = false);        
    }else{
      type === 'surcharge' ? (this.isSurChargePercentageSplit100 = true) : (this.isPercentageSplit100 = true); 
    }
  }

  validateChargeBackFields(event, rate?) {
    const isPaste = event.type === 'paste';

    if (isPaste) {
      const pasteData = event.clipboardData?.getData('text') || '';
      if (pasteData.includes('.')) {
        event.preventDefault();
        return false;
      }
    }
    if(event.charCode === 46){
      event.preventDefault();
      return false;
    }
    if (
      (event.charCode >= 48 && event.charCode <= 57) ||
      (!rate && event.charCode == 45) 
    ) {
      if (event.target.value.indexOf(".") >= 0) {
        return event.target.value.length -
          event.target.value.indexOf(".") -
          1 <=
          1
          ? true
          : false;
      }
      return true;
    }
    return false;
  }

  validateWbsCodesPercentage(){
    this.isPercentageSplit100 = true;
    let totalPercentage = 0;
    this.pricingForm.get('WBSCodesList')['controls']
      .forEach(control => {
        if(control.controls.wbscode.value === "" && this.WBSCodesList.length > 1){
          control.controls.wbscode.setErrors({'isEmpty': true});
          this.isWbsFormatError = true;
        }
        if(control.controls.Description.value === "" && this.WBSCodesList.length > 1){
          this.isPercentageSplit100 = false;
        }else {
          totalPercentage = +totalPercentage + (+control.controls['Description'].value);
        }
       
      })
      if(this.WBSCodesList.length > 1 && totalPercentage !== 100){
          this.isPercentageSplit100 = false;  
      }
    }

    checkWbsCodesErrors(values,type:string):void{
      const list = type === 'surcharge' ? this.SurchargeWBSCodesList : this.WBSCodesList;
      const tempArray = [];
      this.formatErrorMessage = "";
      values.forEach(control =>{
        
        if(control.controls.wbscode.hasError('pattern')){
          type === 'surcharge' ? (this.isSurchargeFormatError = true) : (this.isWbsFormatError = true);
          control.controls.wbscode.setErrors({'isEmpty': true});
        }else if(!control.controls.wbscode.hasError('pattern') && !(control.controls.wbscode.value === "" && list.length > 1)) {
          control.controls.wbscode.setErrors(null);
          type === 'surcharge' ? (this.isSurchargeFormatError = false) : (this.isWbsFormatError = false)
        }
        control.controls.wbscode.updateValueAndValidity();
        tempArray.push(control.controls.wbscode.value);
      })
      const isDuplicate = tempArray.some(function(item, idx){ 
      return tempArray.indexOf(item) != idx 
      });
      if(isDuplicate){
        values.forEach(control =>{
          control.controls.wbscode.setErrors({'isEmpty': true});
        })
        type === 'surcharge' ? (this.isDuplicateSurchargeWbsCodes = true) : (this.isDuplicateWbsCodes = true)
      }else{
        type === 'surcharge' ? (this.isDuplicateSurchargeWbsCodes = false ) : (this.isDuplicateWbsCodes = false)
      }
    }

    validateSurChargeWbsCodesPercentage():void{
      this.isSurChargePercentageSplit100 = true;
      let totalPercentage = 0;
      this.pricingForm.get('SurchargeWBSCodesList')['controls']
        .forEach(control => {
          if(control.controls.wbscode.value === "" && this.SurchargeWBSCodesList.length > 1){
            control.controls.wbscode.setErrors({'isEmpty': true});
            this.isSurchargeFormatError = true;
          }
          if(control.controls.Description.value === "" && this.SurchargeWBSCodesList.length > 1){
            this.isSurChargePercentageSplit100 = false;
          }else {
            totalPercentage = +totalPercentage + (+control.controls['Description'].value);
          }
          control.controls.wbscode.updateValueAndValidity();
        })
        if(this.SurchargeWBSCodesList.length > 1 && totalPercentage !== 100){
            this.isSurChargePercentageSplit100 = false;
        }
      }

  // WBS Typeahead observable for pricing modal (scoped per input row)
  getWbsCodeSearch(itemId: number, isSurcharge: boolean = false) {
    const key = this.getWbsRowKey(itemId, isSurcharge);
    if (!this.wbsTypeaheadHandlers[key]) {
      this.wbsTypeaheadHandlers[key] = (text$: Observable<string>) =>
        text$.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => {
            this.searchingWBS = true;
            this.setWbsRowSearching(itemId, isSurcharge, true);
          }),
          switchMap((term) => {
            if (!term || term.length < 22) {
              this.searchingWBS = false;
              this.setWbsRowSearching(itemId, isSurcharge, false);
              return of([]);
            }
            return this.addSubscriptionService.getWBSCodes(term).pipe(
              tap(() => (this.searchFailedWBS = false)),
              catchError(() => {
                this.searchFailedWBS = true;
                this.setWbsRowSearching(itemId, isSurcharge, false);
                return of([]);
              })
            );
          }),
          tap(() => {
            this.searchingWBS = false;
            this.setWbsRowSearching(itemId, isSurcharge, false);
          })
        );
    }
    return this.wbsTypeaheadHandlers[key];
  }

  // Format WBS for display in typeahead dropdown
  formatWBS = (wbs: any) => {
    if (wbs && typeof wbs === 'object') {
      // Use Element field from new API response structure
      return wbs.Element || wbs.WBSCode || wbs.wbsCode || '';
    }
    return wbs || '';
  };

  // Handle WBS selection from typeahead dropdown
  onWBSSelect(event: any, itemId: number, isSurcharge?: boolean) {
    if (event && event.item) {
      // Prevent ngbTypeahead from setting the model to the object
      event.preventDefault();
      this.setWbsRowSearching(itemId, !!isSurcharge, false);
      
      const selectedWBS = event.item;
      // Use Element and DunsNumber from new API response structure
      const wbsCode = selectedWBS.Element || selectedWBS.WBSCode || selectedWBS.wbsCode || '';
      const wbsDunsID = selectedWBS.DunsNumber || selectedWBS.DunsID || selectedWBS.dunsId || '';
      
      // Update the WBS code in form array
      const formArray = isSurcharge ? this.SurchargeWBSCodesList : this.WBSCodesList;
      const controls = this.pricingForm.get(isSurcharge ? 'SurchargeWBSCodesList' : 'WBSCodesList')['controls'];
      
      if (controls && controls[itemId]) {
        controls[itemId].controls['wbscode'].setValue(wbsCode);
        // Mark as selected from picklist
        controls[itemId]['isSelectedFromPicklist'] = true;
        controls[itemId]['selectedWbsCode'] = this.normalizeWbsCode(wbsCode);
        controls[itemId]['isWbsActive'] = true;
        controls[itemId]['isWbsStatusChecked'] = true;
        controls[itemId]['wbsDunsNumber'] = wbsDunsID;
        controls[itemId].updateValueAndValidity();
      }
    }
  }

  // Handle WBS value changes - clear selection status if code is edited after selection
  onWBSValueChange(itemId: number, isSurcharge: boolean): void {
    const controls = this.pricingForm.get(isSurcharge ? 'SurchargeWBSCodesList' : 'WBSCodesList')['controls'];
    
    if (controls && controls[itemId]) {
      const currentValue = this.normalizeWbsCode(controls[itemId].controls['wbscode'].value);
      const selectedValue = controls[itemId]['selectedWbsCode'];
      
      // If current value differs from the selected value, clear the selection flags
      if (selectedValue && currentValue !== selectedValue) {
        controls[itemId]['isSelectedFromPicklist'] = false;
        controls[itemId]['isWbsActive'] = false;
        controls[itemId]['isWbsStatusChecked'] = false;
      } else if (!selectedValue && currentValue) {
        // If there's a current value but no selectedValue, also clear flags
        controls[itemId]['isSelectedFromPicklist'] = false;
      }
      
      controls[itemId].updateValueAndValidity();
    }
  }

  save(){
      this.isSubmitted = true;
      this.validateSFDCDisableOtherReason();
      this.validateWbsCodesPercentage();
      if(this.isEditMode && this.editData && +this.editData['Surcharge'] > 0){
        this.validateSurChargeWbsCodesPercentage();
    }
    
      // Validate WBS mandatory and picklist requirements
      this.validateWBSMandatory();
      
    if (this.pricingForm.controls['WBSCodesList'].status == "INVALID") {
      this.pricingForm.controls['WBSCodesList'].setErrors({ required: true });
      this.pricingForm.updateValueAndValidity(); 
      }
      if (this.invalidNumberOfUsers) {
      this.pricingForm.controls['NoOfUsers'].setErrors({ required: true });  
      this.pricingForm.updateValueAndValidity();  
      }
      
      // Check if any WBS codes are inactive (grey tick) and if WBS is mandatory
      // If mandatory and WBS is inactive, prevent save
      if (this.isWBSMandatory && this.isStatusActive && this.isWbsInactive) {
        return; // Don't allow save if mandatory WBS is inactive
      }
      
      // Check if mandatory WBS was not picked from list
      if (this.isWBSMandatory && this.wbsNotPickedFromList) {
        return; // Don't allow save if mandatory WBS wasn't picked from list
      }
      
      // Check if WBS codes are present and confirmation checkbox is required
      if (this.hasValidWBSCodes() && !this.IsWBSCodeConfirmed) {
        return; // Don't allow save if WBS codes exist but not confirmed
      }
      
      if(!this.isSurchargeFormatError && !this.isWbsFormatError && !this.isDuplicateWbsCodes && !this.isDuplicateSurchargeWbsCodes &&
         this.isPercentageSplit100 && this.isSurChargePercentageSplit100 && this.pricingForm.valid){
        const pricingData = this.pricingForm.getRawValue();
        pricingData.ActualUsage = this.editData && this.editData.ActualUsage ? this.editData.ActualUsage : 0;
        pricingData.SFEvent = this.editData && this.editData.SFEvent ? this.editData.SFEvent : '';
        pricingData.CreatedDate = this.editData && this.editData.CreatedDate ? this.editData.CreatedDate : '';
        pricingData.CreatedBy = this.editData && this.editData.CreatedBy ? this.editData.CreatedBy : '';
        pricingData.ModifiedDate = this.editData && this.editData.ModifiededDate ?  this.editData.ModifiededDate : '';
        pricingData.ModifiedBy = this.editData && this.editData.ModifiedBy ? this.editData.ModifiedBy : '';
        pricingData.WBSCodes = '';
        pricingData.SubscriptionID = 0;
        pricingData.id = this.type === 'add' && !(this.isOpenPricing) ? this.tableLength + 1: this.editData.id;
        pricingData.EventID = `${pricingData.EventID || ''}`.trim();
        pricingData.EventName = `${pricingData.EventName || ''}`.trim();
        pricingData.DisableSFDCIntegration = !!pricingData.DisableSFDCIntegration;
        pricingData.SFDCDisableReasonId = pricingData.DisableSFDCIntegration
          ? pricingData.SFDCDisableReasonId
          : null;
        pricingData.SFDCDisableOtherReason =
          pricingData.DisableSFDCIntegration && +pricingData.SFDCDisableReasonId === 4
            ? `${pricingData.SFDCDisableOtherReason || ''}`.trim()
            : null;
        if (!(this.isOpenPricing)) {
              
          pricingData.SurchargeWBSCodes = '';
          pricingData.PricePerPerson = pricingData.PricePerPerson === null ? 80 : +pricingData.PricePerPerson;
          pricingData.ChargeBack = (this.pricingForm.controls['ChargeBack'].value == 0 || this.pricingForm.controls['ChargeBack'].value == "") ?
          ( (pricingData.PricePerPerson * pricingData.NoOfUsers)%1 !== 0 ? (pricingData.PricePerPerson * pricingData.NoOfUsers).toFixed(2) :(pricingData.PricePerPerson * pricingData.NoOfUsers)) : 
           this.pricingForm.controls['ChargeBack'].value;
          pricingData.Surcharge = this.editData && this.editData.Surcharge ? this.pricingForm.controls['Surcharge'].value : 0;
          if(this.isEditMode && (pricingData.NoOfUsers < this.editData.ActualUsage)){
            pricingData.Surcharge = (this.editData.ActualUsage - pricingData.NoOfUsers) * pricingData.PricePerPerson
          }  
          if (this.isEditMode && pricingData.Surcharge <= 0) {
            pricingData.SurchargeWBSCodesList = [];
          }
        } else {
          pricingData.FiscalYear = this.editData.FiscalYear ? this.editData.FiscalYear : '';
          pricingData.QuarterOrPeriod = this.editData.QuarterOrPeriod ? this.editData.QuarterOrPeriod : '';
          pricingData.PricePerPerson = this.pricingForm.controls['PricePerPerson'].value ? this.pricingForm.controls['PricePerPerson'].value : 80;
          pricingData.ChargeBack = this.pricingForm.controls['ChargeBack'].value;
          pricingData.StartDate = this.editData.StartDate;
          pricingData.EndDate = this.editData.EndDate;
        }

        // Preserve the isExistingWbs / isWbsActive / isWbsStatusChecked flags on the emitted
        // WBS data so that the parent subscription form's validateWBSCodesInPricing() continues
        // to treat inactive-but-already-linked codes as non-blocking on submission.
        const wbsCtrlArray = this.pricingForm.get('WBSCodesList');
        if (wbsCtrlArray && pricingData.WBSCodesList) {
          (wbsCtrlArray['controls'] as any[]).forEach((ctrl, i) => {
            if (pricingData.WBSCodesList[i]) {
              pricingData.WBSCodesList[i]['isExistingWbs'] = ctrl['isExistingWbs'] || false;
              pricingData.WBSCodesList[i]['isWbsActive'] = ctrl['isWbsActive'];
              pricingData.WBSCodesList[i]['isWbsStatusChecked'] = ctrl['isWbsStatusChecked'] || false;
            }
          });
        }
        const surchargeCtrlArray = this.pricingForm.get('SurchargeWBSCodesList');
        if (surchargeCtrlArray && pricingData.SurchargeWBSCodesList) {
          (surchargeCtrlArray['controls'] as any[]).forEach((ctrl, i) => {
            if (pricingData.SurchargeWBSCodesList[i]) {
              pricingData.SurchargeWBSCodesList[i]['isExistingWbs'] = ctrl['isExistingWbs'] || false;
              pricingData.SurchargeWBSCodesList[i]['isWbsActive'] = ctrl['isWbsActive'];
              pricingData.SurchargeWBSCodesList[i]['isWbsStatusChecked'] = ctrl['isWbsStatusChecked'] || false;
            }
          });
        }

        pricingData.IsWBSCodeConfirmed = this.IsWBSCodeConfirmed;
        this.pricingDetails.emit(pricingData);
         this.activeModal.close();
    }
  }

  validateWBSMandatory() {
    this.wbsNotPickedFromList = false;
    this.isWbsInactive = false;
    this.inactiveWbsCode = '';

    const markControlNotPicked = (control: any) => {
      const wbscodeControl = control?.controls?.wbscode;
      if (!wbscodeControl) {
        return;
      }
      const existingErrors = wbscodeControl.errors || {};
      wbscodeControl.setErrors({ ...existingErrors, notPickedFromList: true });
      wbscodeControl.markAsTouched();
    };

    const clearNotPickedError = (control: any) => {
      const wbscodeControl = control?.controls?.wbscode;
      if (!wbscodeControl || !wbscodeControl.errors) {
        return;
      }
      const { notPickedFromList, ...remainingErrors } = wbscodeControl.errors;
      wbscodeControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    };

    const isCodeSelectedFromPicklist = (control: any, currentCode: string): boolean => {
      return (
        control['isSelectedFromPicklist'] === true &&
        this.normalizeWbsCode(control['selectedWbsCode']) === this.normalizeWbsCode(currentCode)
      );
    };

    const isModifiedExistingCode = (control: any, currentCode: string): boolean => {
      if (control['isExistingWbs'] !== true) {
        return false;
      }
      const originalCode = this.normalizeWbsCode(control['originalWbsCode']);
      const normalizedCurrent = this.normalizeWbsCode(currentCode);
      if (!originalCode || !normalizedCurrent) {
        return false;
      }
      return originalCode !== normalizedCurrent;
    };
    
    // Check regular WBS codes
    const wbsControlsFormArray = this.pricingForm.get('WBSCodesList');
    if (wbsControlsFormArray) {
      const wbsControls = wbsControlsFormArray['controls'];
      if (wbsControls && wbsControls.length > 0) {
        wbsControls.forEach((control: any) => {
          const wbsCode = control.controls['wbscode'].value;

          if (wbsCode && wbsCode.trim()) {
            // WBS code is filled in
            if (this.isWBSMandatory) {
              const requiresPicklistSelection =
                control['isExistingWbs'] !== true || isModifiedExistingCode(control, wbsCode);

              if (requiresPicklistSelection && !isCodeSelectedFromPicklist(control, wbsCode)) {
                this.wbsNotPickedFromList = true;
                markControlNotPicked(control);
              } else {
                clearNotPickedError(control);
              }

              // For mandatory WBS: check if it's inactive – but only for NEW codes.
              // Existing (already linked) inactive codes should not block saving.
              if (
                this.isStatusActive &&
                control['isWbsStatusChecked'] &&
                !control['isWbsActive'] &&
                !(this.isSubscriptionCreatedBeforeMdmWbsDeploymentDate && control['isExistingWbs'])
              ) {
                this.isWbsInactive = true;
                this.inactiveWbsCode = wbsCode;
              }
            }
          } else {
            clearNotPickedError(control);
          }
        });
      }
    }
    
    // Also check surcharge WBS codes if present
    const surchargeControlsFormArray = this.pricingForm.get('SurchargeWBSCodesList');
    if (surchargeControlsFormArray) {
      const surchargeControls = surchargeControlsFormArray['controls'];
      if (surchargeControls && surchargeControls.length > 0) {
        surchargeControls.forEach((control: any) => {
          const wbsCode = control.controls['wbscode'].value;
          if (wbsCode && wbsCode.trim()) {
            // Only raise inactive error for NEW (non-existing) surcharge WBS codes
            if (
              this.isStatusActive &&
              control['isWbsStatusChecked'] &&
              !control['isWbsActive'] &&
              !(this.isSubscriptionCreatedBeforeMdmWbsDeploymentDate && control['isExistingWbs'])
            ) {
              this.isWbsInactive = true;
              this.inactiveWbsCode = wbsCode;
            }

            if (this.isWBSMandatory) {
              const requiresPicklistSelection =
                control['isExistingWbs'] !== true || isModifiedExistingCode(control, wbsCode);
              if (requiresPicklistSelection && !isCodeSelectedFromPicklist(control, wbsCode)) {
                this.wbsNotPickedFromList = true;
                markControlNotPicked(control);
              } else {
                clearNotPickedError(control);
              }
            }
          } else {
            clearNotPickedError(control);
          }
        });
      }
    }
  }

  enableDisableAddWbsCode(): void{
    this.checkPercentage(this.WBSCodesList.controls, 'chargeback');
    if(this.WBSCodesList.length > 1){
      if(this.pricingForm.get('WBSCodesList')['controls'][this.WBSCodesList.length - 1].controls.wbscode.value !== "" && this.pricingForm.get('WBSCodesList')['controls'][this.WBSCodesList.length - 1].controls.Description.value !== ""){
       this.isAddWbsCodes = true
      }else{
        this.isAddWbsCodes = false;
      }
    }else {
      if(this.pricingForm.get('WBSCodesList')['controls'][this.WBSCodesList.length - 1].controls.wbscode.value !== ""){
        this.isAddWbsCodes = true
      }else{
        this.isAddWbsCodes = false;
      }
    }

    if (this.isPercentageSplit100) {
      this.isAddWbsCodes = false;
    }
  }

   enableDisableSurchargeWbsCodes(): void {
    if(this.SurchargeWBSCodesList.length > 1){
      if(this.pricingForm.get('SurchargeWBSCodesList')['controls'][this.SurchargeWBSCodesList.length - 1].controls.wbscode.value !== "" && this.pricingForm.get('SurchargeWBSCodesList')['controls'][this.SurchargeWBSCodesList.length - 1].controls.Description.value !== ""){
       this.isAddSurChargeWbsCodes = true
      }else{
        this.isAddSurChargeWbsCodes = false;
      }
    }else {
      if(this.pricingForm.get('SurchargeWBSCodesList')['controls'][this.SurchargeWBSCodesList.length - 1].controls.wbscode.value !== ""){
        this.isAddSurChargeWbsCodes = true
      }else{
        this.isAddSurChargeWbsCodes = false;
      }
    }
   }
   onNoOfUsersChange(event):void {
    this.calculateChargeback();
  }

  onSpecifyChange(): void {

  }

  onPriceChange(event):void {
    if(this.pricingForm.controls['ChargeBack']){
      this.isOpenPricing ? this.calculateOpenChargeback() : this.calculateChargeback();
    }
    
  }

  calculateOpenChargeback(): void {
    const actualUsage = this.pricingForm.controls['ActualUsage'];
    const pricePerPerson = this.pricingForm.controls['PricePerPerson']
    if(actualUsage.value != 0 && pricePerPerson.value != 0 && pricePerPerson.value !== "" && actualUsage.value !== ""){
      this.pricingForm.controls['ChargeBack'].setValue(((+pricePerPerson.value * actualUsage.value)%1 !== 0 ? (+pricePerPerson.value * actualUsage.value).toFixed(2) : (+pricePerPerson.value * actualUsage.value) ))
    }else {
      this.pricingForm.controls['ChargeBack'].setValue(0)
    }
    
  }
  calculateChargeback():void {
    const noOfUser = this.pricingForm.controls['NoOfUsers'];
    const pricePerPerson = this.pricingForm.controls['PricePerPerson'];
    const actualUsage = this.editData ? this.editData.ActualUsage : 0;
    if(noOfUser.value !== null && noOfUser.value !== 0 &&  pricePerPerson.value !== "" && pricePerPerson.value !== null){
      this.pricingForm.controls['ChargeBack'].setValue(((+pricePerPerson.value * noOfUser.value)%1 !== 0  ? (+pricePerPerson.value * noOfUser.value).toFixed(2) : (+pricePerPerson.value * noOfUser.value) ))
    }else{
      this.pricingForm.controls['ChargeBack'].setValue("")
    }
    
    if(this.isEditMode && +this.editData['Surcharge'] > 0){
      const temp = ((+actualUsage - (+noOfUser.value)) * +pricePerPerson.value);
      this.pricingForm.controls['Surcharge'].setValue(temp);
    }
  }

  allowNumbersOnly(e): void {
    const code = (e.which) ? e.which : e.keyCode;
    if (code > 31 && (code < 48 || code > 57 )) {
        e.preventDefault();
    }
  }

}
